import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import InboxIcon from "@mui/icons-material/Inbox";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosClient from "../../../../../lib/axios";
import PrintIcon from "@mui/icons-material/Print";
import DoneIcon from "@mui/icons-material/Done";
import DeleteIcon from "@mui/icons-material/Delete";
import { enqueueSnackbar } from "notistack";
import { useConfirm } from "material-ui-confirm";
import { useMemo, useState } from "react";
import TableMenuOptions from "../../../../../components/TableMenuOptions";
import LoadingApiQuery from "../../../../../components/LoadingApiQuery";
import ChipWithTooltip from "../../../../../components/ChipWithTooltip";
import { IDataProcurarExpedicoes } from "../../../../../types/api/estoque/pesquisar-expedicoes";
import { IDataLojas } from "../../../../../types/api/geral/pesquisar-lojas";
import EditIcon from "@mui/icons-material/Edit";
import DialogEditExpedicao from "./DialogEditExpedicao";
import DialogConcluirPedido from "./DialogConcluirPedido";
import useImprimirArquivo from "../../../../../lib/useImprimirArquivo";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import DialogFilter from "./DialogFilter";
import { Moment } from "moment";
import IRequest from "../../../../../types/IRequest";
import { SwapHoriz } from "@mui/icons-material";
import DialogTransferencia from "./DialogTransferencia";
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import DialogEmitirNfe from "./DialogEmitirNfe";
import CancelIcon from '@mui/icons-material/Cancel';
import DialogReverterConclusao from "./DialogReverterConclusao";
import { usePermission } from "../../../../../components/PermissionGuard";

export default function Expedicoes({ lojaSelecionadaId }: IExpedicoesProps) {
  const queryClient = useQueryClient();
  const imprimirArquivo = useImprimirArquivo();

  const confirm = useConfirm();
  const permissaoReverterConclusao = usePermission("estoque.ressuprimento.reverter-expedicao");

  const [openEdit, setOpenEdit] = useState<number | null>(null);
  const [openDialogTransferencia, setOpenDialogTransferencia] = useState<{ id: number; status: string } | null>(null);
  const [openConcluirPed, setOpenConcluirPed] = useState<number | null>(null);
  const [openReverterConclusao, setOpenReverterConclusao] = useState<number | null>(null);
  const [openEmitirNfe, setOpenEmitirNfe] = useState<{ expedId: number, transportId: number } | null>(null);
  const [openDialogFilter, setOpenDialogFilter] = useState(false);
  const [filters, setFilters] = useState<FilterProps>(defaultFilters);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);

  //queries e mutations
  const { data, isFetching } = useQuery({
    queryKey: [
      "pesquisar-expedicoes",
      filters,
      lojaSelecionadaId,
      page,
      rowsPerPage,
    ],
    queryFn: () =>
      axiosClient.get("/estoque/pesquisar-expedicoes", {
        params: {
          loja_id: lojaSelecionadaId,
          ...(filters.dataCriacao && { data_inicio: filters.dataCriacao.format("YYYY-MM-DD") }),
          ...(filters.dataFim && { data_fim: filters.dataFim.format("YYYY-MM-DD") }),
          ...(filters.numeroPedido && { numero_pedido: filters.numeroPedido }),
          ...(filters.lojaSolicitante && { loja_solicitante_id: filters.lojaSolicitante.id }),
          ...(filters.codigoCS9 && { codigo_cs9: filters.codigoCS9 }),
          page: page + 1,
          limit: rowsPerPage,
        },
      }),
    cacheTime: 0,
    retry: false,
    enabled: Boolean(lojaSelecionadaId),
  });

  const { req, data: expedicoes } = useMemo<IListaExpedicoes>(
    () => ({
      req: data?.data ?? null,
      data: data?.data.data ?? [],
    }),
    [data]
  );

  const { mutate: excluirExpedicao } = useMutation({
    mutationKey: "excluir-expedicao",
    mutationFn: (id: Number) =>
      axiosClient.delete("/estoque/excluir-expedicao/" + id),
    onError: (e) => {
      console.error(e);
      enqueueSnackbar("Erro ao excluir expedição", { variant: "error" });
    },
    onSuccess: () => {
      enqueueSnackbar("Expedição excluída com sucesso", { variant: "success" });
      queryClient.invalidateQueries("pesquisar-expedicoes");
      queryClient.invalidateQueries("pesquisar-itens-para-expedir");
    },
  });
  //

  //Funções manipuladoras
  const handleImprimir = (id: number) => {
    imprimirArquivo(`estoque/imprimir-expedicao/${id}`);
  };
  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const statusLabels = useMemo(() => {
    return expedicoes?.map((i) => {
      if (i.concluido_por && i.transferido_por) {
        return `${i.status} por ${i.transferido_por}`;
      }
      if (i.concluido_por && !i.transferido_por) {
        return `${i.status} por ${i.concluido_por}`;
      }
      if (!i.concluido_por && !i.transferido_por) {
        return `${i.status}`;
      }
      return '';
    });
  }, [expedicoes]);


  return (
    <Box component={Paper} height="100%" p={2} pb={5}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={3}
        pt={1}
        pb={1}
      >
        <Typography variant="h6">Expedições</Typography>

        <Stack
          alignItems="flex-end"
          justifyContent="center"
          direction="row"
          gap={2}
        >
          <IconButton
            sx={{ p: 0 }}
            aria-label="filter"
            onClick={() => setOpenDialogFilter((prev) => !prev)}
          >
            <TuneOutlinedIcon />
          </IconButton>
        </Stack>
      </Box>

      {isFetching && <LoadingApiQuery />}

      {!isFetching && (
        <>
          {expedicoes.length > 0 && (
            <>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>Pedido</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Destinatário
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Data de criação
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expedicoes.map((i, index) => {
                    return (
                      <TableRow>
                        <TableCell sx={{ maxWidth: "220px" }}>
                          <Box mb={0.5}>
                            #{i.pedidos[0]}{" "}
                            {i.pedidos.length > 1 && (
                              <ChipWithTooltip
                                stringPrefix="#"
                                extraLabels={i.pedidos.slice(1)}
                                label={`+${i.pedidos.length - 1}`}
                              />
                            )}
                          </Box>

                          <Chip
                            size="small"
                            variant="outlined"
                            label={statusLabels?.[index]}
                            color={
                              i.status === "Em separação" ? "warning" : "primary"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Typography>{i.destinatario}</Typography>
                          <Typography
                            component="div"
                            variant="caption"
                            color="gray"
                          >
                            Separador: {i.nome_separador}
                          </Typography>
                          {i.pedido_cs9 && (
                            <Typography variant="caption" fontWeight="bold">
                              Pedido CS9: Tipo 8 / Cód {i.pedido_cs9}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography>{i.criado_em}</Typography>
                          <Typography variant="caption">
                            por {i.criado_por}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: "155px" }}>
                          <Stack direction="row" spacing={1}>
                            <Tooltip title="Imprimir">
                              <IconButton onClick={() => handleImprimir(i.id)}>
                                <PrintIcon />
                              </IconButton>
                            </Tooltip>

                            {
                              i.status === "Em separação" && (
                                <Tooltip title="Editar">
                                  <IconButton onClick={() => setOpenEdit(i.id)}>
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                              )
                            }

                            {(
                              i.status === "Concluída" && i.valido_para_transferencia
                            ) && (
                                <>
                                  <Tooltip title="Gerar transferência">
                                    <IconButton onClick={() => setOpenDialogTransferencia({ id: i.id, status: i.status }
                                    )}>
                                      <SwapHoriz />
                                    </IconButton>
                                  </Tooltip>
                                  {
                                    permissaoReverterConclusao &&
                                    <Tooltip title="Reverter conclusão">
                                      <IconButton
                                        onClick={() => setOpenReverterConclusao(i.id)}
                                      >
                                        <CancelIcon />
                                      </IconButton>
                                    </Tooltip>
                                  }
                                </>
                              )
                            }

                            {
                              i.status === 'Concluída' && !i.valido_para_transferencia &&
                              <>
                                <Tooltip title="Emitir nota fiscal">
                                  <IconButton
                                    disabled={!Boolean(i.transportador)}
                                    onClick={() => setOpenEmitirNfe({ expedId: i.id, transportId: i.transportador?.id || 0 })}
                                  >
                                    <RequestQuoteOutlinedIcon />
                                  </IconButton>
                                </Tooltip>
                                {
                                  permissaoReverterConclusao &&
                                  <Tooltip title="Reverter conclusão">
                                    <IconButton
                                      onClick={() => setOpenReverterConclusao(i.id)}
                                    >
                                      <CancelIcon />
                                    </IconButton>
                                  </Tooltip>
                                }
                              </>
                            }

                            {
                              i.status === "Em separação" && (
                                <TableMenuOptions
                                  menuItems={[
                                    {
                                      label: "Concluir",
                                      action: () => setOpenConcluirPed(i.id),
                                      icon: <DoneIcon sx={{ color: "gray" }} />,
                                    },
                                    {
                                      label: "Excluir",
                                      action: () => {
                                        confirm({
                                          title: "Excluir",
                                          description:
                                            "Deseja realmente excluir essa expedição?",
                                        }).then(() => {
                                          excluirExpedicao(i.id);
                                        });
                                      },
                                      icon: (
                                        <DeleteIcon sx={{ color: "gray" }} />
                                      ),
                                    },
                                  ]}
                                />
                              )
                            }
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                p={2}
              >
                <TablePagination
                  rowsPerPageOptions={[5, 10, 20]}
                  component="div"
                  count={req?.total ?? 0}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Linhas por página"
                  labelDisplayedRows={() =>
                    `Pag. ${page + 1}`
                  }
                />
              </Box>
            </>
          )}
        </>
      )}

      {expedicoes.length === 0 && !isFetching && (
        <Box
          height="90%"
          minHeight={200}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          border={1}
          borderColor="#1b5e20"
          borderRadius={2}
        >
          <InboxIcon fontSize="large" color="primary" />
          <Typography color="#1b5e20">
            Selecione pedidos ao lado para iniciar o processo separação de
            produtos
          </Typography>
        </Box>
      )}

      {openConcluirPed && (
        <DialogConcluirPedido
          open={openConcluirPed}
          handleClose={() => setOpenConcluirPed(null)}
        />
      )}

      {
        openReverterConclusao && (
          <DialogReverterConclusao
            open={openReverterConclusao}
            handleClose={() => setOpenReverterConclusao(null)}
          />
        )
      }

      {openEdit && (
        <DialogEditExpedicao
          open={openEdit}
          handleClose={() => setOpenEdit(null)}
        />
      )}

      {openDialogTransferencia && (
        <DialogTransferencia
          loja_id={lojaSelecionadaId}
          expedicao_id={openDialogTransferencia.id}
          status={openDialogTransferencia.status}
          handleClose={() => setOpenDialogTransferencia(null)}
        />
      )}

      {openEmitirNfe && (
        <DialogEmitirNfe
          open={Boolean(openEmitirNfe)}
          expedicaoId={openEmitirNfe.expedId}
          transportadorId={openEmitirNfe.transportId}
          handleClose={() => setOpenEmitirNfe(null)}
        />
      )}

      {openDialogFilter && (
        <DialogFilter
          defaultValues={filters}
          openDialogFilter={openDialogFilter}
          onClose={() => setOpenDialogFilter(false)}
          onSubmit={(data) => {
            setFilters(data);
            setPage(0);
            setOpenDialogFilter(false);
          }}
          onClear={() => {
            setFilters(defaultFilters);
            setPage(0);
            setOpenDialogFilter(false);
          }}
        />
      )}
    </Box>
  );
}

const defaultFilters = {
  dataCriacao: null,
  dataFim: null,
  numeroPedido: "",
  lojaSolicitante: null,
  codigoCS9: "",
};

export interface FilterProps {
  dataCriacao: Moment | null;
  dataFim: Moment | null;
  numeroPedido: string;
  lojaSolicitante: IDataLojas | null;
  codigoCS9: string;
}

interface IExpedicoesProps {
  lojaSelecionadaId: number;
}

interface IListaExpedicoes {
  req: IRequest<IDataProcurarExpedicoes> | null;
  data: IDataProcurarExpedicoes[];
}
